import React, {Fragment, useContext, useEffect, useMemo, useState} from 'react';
import {Form, FormGroup} from 'reactstrap';
import {useForm} from "react-hook-form";
import axiosConfig from "../../../../Config/axios";
import {Btn} from "../../../../AbstractElements";
import Loader from "../../../../Layout/Loader";
import TextInput from "../../../../CommonElements/Input/TextInput";
import BackendDropDown from "../../../../CommonElements/DropDown/BackendDropDown";
import AuthorizationContext from "../../../../_helper/Authorization";
import {getSystemCoreRoute} from "../../../../Route/RouteIndex";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";
import type {ContactFormProps, ContactFormValues} from "../Types/ContactTypes";

const ContactForm: React.FC<ContactFormProps> = ({callBack, contact, related_key, related_id}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [selectedContactTypeKey, setSelectedContactTypeKey] = useState<string>('');

    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler, hasPermission} = authContext || {};
    const canSubmit = contact
        ? (hasPermission ? hasPermission('update-contact') : false)
        : (hasPermission ? hasPermission('create-contact') : false);

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: {errors},
        watch,
        reset,
        getValues,
    } = useForm<ContactFormValues>();

    const selectedContactTypeId = watch('contact_type_id');

    useEffect(() => {
        if (contact) {
            const contactTypeId = contact?.contact_type?.id || contact?.contactType?.id || contact?.contact_type_id;
            reset({
                contact_type_id: contactTypeId,
                contact: contact?.contact,
            });
            setSelectedContactTypeKey(contact?.contact_type?.key || contact?.contactType?.key || '');
            return;
        }

        reset({
            contact_type_id: undefined,
            contact: '',
        });
        setSelectedContactTypeKey('');
    }, [contact, reset]);

    const selectedContactValidationType = useMemo(() => {
        const key = selectedContactTypeKey || '';
        if (key.includes('mobile')) {
            return 'mobile';
        }
        if (key.includes('email')) {
            return 'email';
        }
        return 'text';
    }, [selectedContactTypeKey]);

    useEffect(() => {
        if (!selectedContactTypeId) {
            setSelectedContactTypeKey('');
        }
    }, [selectedContactTypeId]);

    const onContactTypeChange = (value: any) => {
        setSelectedContactTypeKey(value?.key || value?.payload?.key || '');
    };

    const onSubmit = (data: ContactFormValues) => {
        if (!canSubmit) return;
        clearErrors();
        setLoaderVisibility(true);
        if (contact) {
            updateContact(data);
            return;
        }
        createContact(data);
    };

    const createContact = (data: ContactFormValues) => {
        const payload = {
            ...data,
            related_key,
            related_id,
        };
        axiosConfig
            .post(getSystemCoreRoute('contact'), payload)
            .then((response: any) => {
                if (response?.data?.data) {
                    callBack?.(response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception, setError as any);
                setLoaderVisibility(false);
            });
    };

    const updateContact = (data: ContactFormValues) => {
        if (!contact) return;
        const payload = {
            ...data,
            related_key: contact.related_key || contact.related_type || related_key,
            related_id: contact.related_id || related_id,
        };
        axiosConfig
            .put(`${getSystemCoreRoute('contact')}/${contact.id}`, payload)
            .then((response: any) => {
                if (response?.data?.data) {
                    callBack?.(response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception, setError as any);
                setLoaderVisibility(false);
            });
    };

    const getContactInputType = () => {
        if (selectedContactValidationType === 'email') return 'email';
        if (selectedContactValidationType === 'mobile') return 'tel';
        return 'text';
    };

    return <Fragment>
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Loader visibility={loaderVisibility}/>
            <FormGroup>
                <BackendDropDown
                    name={'contact_type_id'}
                    label={'Contact Type'}
                    register={register}
                    getValues={getValues}
                    errors={errors}
                    url={'system-core/configuration'}
                    params={{setting_key: 'contact-type'}}
                    onChange={onContactTypeChange}
                    options={{required: true}}
                />
            </FormGroup>
            <TextInput name={'contact'} type={getContactInputType()} label={'Contact'} register={register}
                       errors={errors} placeholder={'Contact value'}
                       options={{
                           required: true,
                           validate: (value: string) => {
                               if (selectedContactValidationType === 'email') {
                                   return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Please provide a valid email.';
                               }
                               if (selectedContactValidationType === 'mobile') {
                                   return /^[0-9+()\-\s]{7,20}$/.test(value) || 'Please provide a valid mobile number.';
                               }
                               return true;
                           }
                       }}/>
            <FormGroup>
                <Btn attrBtn={{
                    className: 'd-block w-100 mt-4',
                    color: 'primary',
                    type: 'submit',
                    disabled: !canSubmit,
                }}>{contact ? 'Update' : 'Create'}</Btn>
            </FormGroup>
        </Form>
    </Fragment>;
};

export default ContactForm;

